import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaCheckCircle, FaTimesCircle, FaMapMarkerAlt, FaPhone, FaEnvelope, FaTimes, FaSearch, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const ItemView = () => {
  const navigate = useNavigate();
  const [scouts, setScouts] = useState([]);
  const [selectedScout, setSelectedScout] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Fetch scouts from API
  useEffect(() => {
    const fetchScouts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(' https://ksa-manager-back.onrender.com/fetch_scouts');
        
        if (response.data.message === "Scouts fetched successfully") {
          setScouts(response.data.scouts);
        } else {
          setError('Failed to fetch scouts data');
        }
      } catch (err) {
        console.error('Error fetching scouts:', err);
        setError('Error connecting to the server');
      } finally {
        setLoading(false);
      }
    };

    fetchScouts();
  }, []);

  // Sort scouts
  const sortScouts = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort scouts
  const filteredScouts = scouts
    .filter(scout => {
      const fullName = `${scout.first_name} ${scout.last_name}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                           scout.region.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterStatus === 'approved') {
        return matchesSearch && scout.status === 'approved';
      } else if (filterStatus === 'pending') {
        return matchesSearch && scout.status === 'pending';
      } else {
        return matchesSearch;
      }
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredScouts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredScouts.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Open modal with scout details
  const openScoutModal = (scout) => {
    setSelectedScout(scout);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedScout(null);
  };

  const handleViewQuestionnaires = (scout) => {
    navigate('/admin/warehouse-view/questionnaires', {
      state: { scout },
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Registered Scouts
        </h1>
        <p className="text-gray-600">
          View and manage all registered academy scouts
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name or region..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
            onClick={() => setFilterStatus('all')}
          >
            All
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'approved'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
            onClick={() => setFilterStatus('approved')}
          >
            Approved
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
            onClick={() => setFilterStatus('pending')}
          >
            Pending
          </button>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="max-w-7xl mx-auto text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading scouts data...</p>
        </div>
      )}

      {error && (
        <div className="max-w-7xl mx-auto bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-8">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Scouts Table */}
      {!loading && !error && (
        <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortScouts('first_name')}
                  >
                    Name {sortConfig.key === 'first_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortScouts('years_of_experience')}
                  >
                    Experience {sortConfig.key === 'years_of_experience' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortScouts('region')}
                  >
                    Location {sortConfig.key === 'region' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortScouts('status')}
                  >
                    Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((scout) => (
                  <tr 
                    key={scout.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openScoutModal(scout)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <FaUser className="text-blue-500" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {scout.first_name} {scout.last_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {scout.specializations}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{scout.years_of_experience} years</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{scout.region}, {scout.district}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{scout.phone}</div>
                      <div className="text-sm text-gray-500">{scout.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        scout.status === 'approved' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {scout.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openScoutModal(scout);
                          }}
                          className="rounded-md bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                        >
                          View Details
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewQuestionnaires(scout);
                          }}
                          className="rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
                        >
                          View Questionnaires
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredScouts.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredScouts.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <FaChevronLeft className="h-5 w-5" />
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => paginate(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === index + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <FaChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scout Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedScout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Scout Details
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Full Name:</div>
                      <div className="font-medium">
                        {selectedScout.first_name} {selectedScout.last_name}
                      </div>
                      
                      <div className="text-gray-600">Date of Birth:</div>
                      <div className="font-medium">
                        {new Date(selectedScout.date_of_birth).toLocaleDateString()}
                      </div>

                      <div className="text-gray-600">Nationality:</div>
                      <div className="font-medium">{selectedScout.nationality}</div>
                      
                      <div className="text-gray-600">Gender:</div>
                      <div className="font-medium capitalize">{selectedScout.gender}</div>
                    </div>
                  </div>
                  
                  {/* Location Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Location Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Region:</div>
                      <div className="font-medium">{selectedScout.region}</div>
                      
                      <div className="text-gray-600">District:</div>
                      <div className="font-medium">{selectedScout.district}</div>
                      
                      <div className="text-gray-600">Town:</div>
                      <div className="font-medium">{selectedScout.town}</div>
                      
                      <div className="text-gray-600">Address:</div>
                      <div className="font-medium">{selectedScout.address || 'Not provided'}</div>
                    </div>
                  </div>
                  
                  {/* Professional Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Professional Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Years of Experience:</div>
                      <div className="font-medium">{selectedScout.years_of_experience}</div>
                      
                      <div className="text-gray-600">Previous Clubs:</div>
                      <div className="font-medium">{selectedScout.previous_clubs}</div>
                      
                      <div className="text-gray-600">Specializations:</div>
                      <div className="font-medium">{selectedScout.specializations}</div>
                      
                      <div className="text-gray-600">Certifications:</div>
                      <div className="font-medium">{selectedScout.certifications}</div>
                    </div>
                  </div>
                  
                  {/* References */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Professional References
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-600">Reference 1</h4>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="text-gray-600">Name:</div>
                          <div className="font-medium">{selectedScout.reference1_name}</div>
                          
                          <div className="text-gray-600">Position:</div>
                          <div className="font-medium">{selectedScout.reference1_position}</div>
                          
                          <div className="text-gray-600">Contact:</div>
                          <div className="font-medium">{selectedScout.reference1_contact}</div>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-gray-600">Reference 2</h4>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div className="text-gray-600">Name:</div>
                          <div className="font-medium">{selectedScout.reference2_name}</div>
                          
                          <div className="text-gray-600">Position:</div>
                          <div className="font-medium">{selectedScout.reference2_position}</div>
                          
                          <div className="text-gray-600">Contact:</div>
                          <div className="font-medium">{selectedScout.reference2_contact}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Registration Details */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Registration Details
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Registration Date:</div>
                      <div className="font-medium">
                        {new Date(selectedScout.registration_date).toLocaleDateString()}
                      </div>
                      
                      <div className="text-gray-600">Reference Number:</div>
                      <div className="font-medium">{selectedScout.registration_reference}</div>
                      
                      <div className="text-gray-600">Status:</div>
                      <div className={`font-medium ${
                        selectedScout.status === 'approved' 
                          ? 'text-green-600' 
                          : 'text-yellow-600'
                      }`}>
                        {selectedScout.status}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ItemView; 